import { createBrowserRouter } from 'react-router-dom';
import { FullStory, isInitialized } from '@fullstory/browser';
import * as R from 'ramda';
import {
  call,
  fork,
  getContext,
  put,
  take,
  takeEvery,
} from 'redux-saga/effects';

import actionTypes from '@atom/actions/actionTypes';
import * as organizationActions from '@atom/actions/organizationActions';
import * as userActions from '@atom/actions/userActions';
import { Snackbar } from '@atom/mui';
import {
  apiErrorHandler,
  getAccountPayload,
  isEmailVerified,
  logoutUser,
  persistAccountPayload,
  tokenExists,
  updateRequestHeaders,
} from '@atom/utilities/accountUtilities';
import api from '@atom/utilities/api';
import authUtilities from '@atom/utilities/authUtilities';
import {
  CURRENT_SUBDOMAIN,
  PASSWORD_RESET_ENDPOINT,
  TOKEN_EXCHANGE_ENDPOINT,
  USER_PROFILE_ENDPOINT,
  VERIFICATION_EMAIL_ENDPOINT,
} from '@atom/utilities/endpoints';

export function* clearUserSession() {
  const router: ReturnType<typeof createBrowserRouter> =
    yield getContext('router');

  if (isInitialized()) {
    FullStory('setIdentity', { anonymous: true });
  }

  yield call(logoutUser);
  yield put(userActions.logoutSuccess());
  yield call(router.navigate, '/login');
}

export function* persistUserSession(action) {
  const router: ReturnType<typeof createBrowserRouter> =
    yield getContext('router');
  try {
    yield call(persistAccountPayload, action.data.token);
    yield put(userActions.persistUserTokenSuccess());

    if (CURRENT_SUBDOMAIN !== 'www') {
      yield call(router.navigate, '/');
    } else {
      yield put(organizationActions.requestUserOrganizations());
      yield call(router.navigate, '/tenant-selection');
    }
  } catch (error) {
    yield fork(apiErrorHandler, error, userActions.persistUserTokenFailure);
  }
}

export function* resendEmailVerificationLink() {
  try {
    Snackbar.info({
      message: 'Email Verification Sent',
    });
    yield call(api.post, VERIFICATION_EMAIL_ENDPOINT);

    yield put(userActions.getEmailVerificationLinkSuccess());
  } catch (error) {
    yield fork(
      apiErrorHandler,
      error,
      userActions.getEmailVerificationLinkFailure,
    );
  }
}

export function* sendPasswordResetEmail(action) {
  try {
    Snackbar.info({
      message: 'Password Reset Email Sent',
    });

    const payload = {
      email: action.data.email,
    };

    yield call(api.post, PASSWORD_RESET_ENDPOINT, payload);
    yield put(userActions.userPasswordResetSuccess());
  } catch (error) {
    yield fork(apiErrorHandler, error, userActions.userPasswordResetFailure);
  }
}

export function* getLoggedInUser() {
  const router: ReturnType<typeof createBrowserRouter> =
    yield getContext('router');
  try {
    if (!tokenExists()) {
      yield call(router.navigate, '/login');
      return;
    }

    if (!isEmailVerified()) {
      yield call(router.navigate, '/verify-email');
      return;
    }

    const { data } = yield call(api.get, USER_PROFILE_ENDPOINT);
    yield call(updateRequestHeaders);

    authUtilities.setUserRole(data.role);
    authUtilities.setUserRoleLevel(data.level);
    authUtilities.setFullStoryAccount(data);

    if (!data.isVerified || !data.isActive) {
      yield call(router.navigate, '/unverified');
    }
    if (!data.id) {
      yield call(router.navigate, '/login');
    }

    yield put(userActions.loggedInUserSuccess(data));
  } catch (error) {
    if (error.response && error.response.status === 403) {
      yield put(userActions.forbiddenUser());
      yield call(router.navigate, '/unverified');
    } else {
      yield put(userActions.loggedInUserFailure());
      yield call(router.navigate, '/login');
    }
  }
}

export function* exchangeAuthToken(action) {
  const router: ReturnType<typeof createBrowserRouter> =
    yield getContext('router');
  try {
    const payload = {
      ...action.data,
    };
    yield call(api.post, TOKEN_EXCHANGE_ENDPOINT, payload);
    yield put(userActions.userAuthTokenExchangeSuccess());

    if (!isEmailVerified()) {
      yield call(router.navigate, '/verify-email');
      return;
    }

    if (CURRENT_SUBDOMAIN !== 'www') {
      yield call(router.navigate, '/');
    } else {
      yield call(router.navigate, '/tenant-selection');
      yield put(organizationActions.requestUserOrganizations());
    }
  } catch (error) {
    yield fork(
      apiErrorHandler,
      error,
      userActions.userAuthTokenExchangeFailure,
    );
  }
}

export function* getUserOrganizations() {
  const router: ReturnType<typeof createBrowserRouter> =
    yield getContext('router');
  try {
    const token = yield call(getAccountPayload);
    const organizations = token.organizations;

    if (R.isEmpty(organizations)) {
      yield call(router.navigate, '/tenant-entry');
    }
    yield put(organizationActions.getUserOrganizationsSuccess(organizations));
  } catch (error) {
    yield fork(
      apiErrorHandler,
      error,
      organizationActions.getUserOrganizationsFailure,
    );
  }
}

/* WATCHERS */

export function* requestUserTokenPersist() {
  yield takeEvery(actionTypes.REQUEST_USER_TOKEN_PERSIST, persistUserSession);
}

export function* requestUserOrganizations() {
  yield takeEvery(actionTypes.REQUEST_USER_ORGANIZATIONS, getUserOrganizations);
}

export function* requestUserPasswordReset() {
  yield takeEvery(
    actionTypes.REQUEST_USER_PASSWORD_RESET,
    sendPasswordResetEmail,
  );
}

export function* requestEmailVerificationLink() {
  yield takeEvery(
    actionTypes.REQUEST_EMAIL_VERIFICATION_LINK,
    resendEmailVerificationLink,
  );
}

export function* requestUserAuthTokenExchange() {
  yield takeEvery(
    actionTypes.REQUEST_USER_AUTH_TOKEN_EXCHANGE,
    exchangeAuthToken,
  );
}

export function* logout() {
  while (true) {
    yield take(actionTypes.TRIGGER_LOGOUT);
    yield fork(clearUserSession);
  }
}

export function* loggedInUser() {
  while (true) {
    yield take(actionTypes.REQUEST_LOGGED_IN_USER);
    yield fork(getLoggedInUser);
  }
}

export default [
  requestUserTokenPersist,
  requestUserPasswordReset,
  requestUserAuthTokenExchange,
  requestUserOrganizations,
  requestEmailVerificationLink,
  logout,
  loggedInUser,
];
